import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import NextTopLoader from 'nextjs-toploader';
// Side effect: sets luxon's Settings.defaultLocale. The root layout's import
// only runs on the server (RSC imports are not bundled for the client), so
// without this the browser falls back to the visitor's own locale for month
// and weekday names and toRelative() — and hydration mismatches with the
// server-rendered strings.
import '@/util/initI18nDates';
import { i18n } from '@/i18n/clientI18n';
import { OTContextProvider } from '@/tracking/onetrust/useOTStatus';
import { GPTScriptsWaitForOneTrust } from '@/tracking/ads/components/GPTScripts';
import { GTMIframe } from '@/tracking/gtm/GTMHeader';
import { FPCHeaderWaitForOneTrust, FPCNoScript } from '@/tracking/facebook';
import { DoubleClickPixel } from '@/tracking/doubleclick/DoubleClickPixel';
import { UnitSelectorProvider } from '@/components/UnitSwitch';
import { AppPromoBanner } from '@/components/common/AppPromoBanner';
import { Toaster } from '@/components/ui/sonner';
import queryClient from '@/root/queryClient';
import { seedWeskiMappings, WeskiMappingSeed } from '@/api/weski/static';
import { SettingsContext, IGlobalSettings } from '@/settings/SettingsContext';

export type ProvidersProps = {
  children: React.ReactNode;
  settings?: IGlobalSettings;
  variant?: 'main' | 'widget';
  weskiMappingsSeed?: WeskiMappingSeed | null;
};

export function Providers({
  children, settings, variant = 'main', weskiMappingsSeed,
}: ProvidersProps) {
  const settingsValue = settings ?? {};

  // Must run synchronously during render (not in an effect, which fires after
  // hydration has already been diffed) so the client's first render sees the
  // same WeSki mapping data the server used - otherwise it starts from an
  // empty cache and produces different markup, causing a hydration mismatch.
  if (weskiMappingsSeed) {
    seedWeskiMappings(weskiMappingsSeed);
  }

  if (variant === 'widget') {
    return (
      <I18nextProvider i18n={i18n}>
        <SettingsContext.Provider value={settingsValue}>
          <QueryClientProvider client={queryClient}>
            {children}
          </QueryClientProvider>
        </SettingsContext.Provider>
      </I18nextProvider>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <SettingsContext.Provider value={settingsValue}>
        <OTContextProvider>
          <GTMIframe />
          <GPTScriptsWaitForOneTrust />
          <FPCNoScript />
          <FPCHeaderWaitForOneTrust />
          <DoubleClickPixel />
          <UnitSelectorProvider>
            <QueryClientProvider client={queryClient}>
              <AppPromoBanner />
              <NextTopLoader showSpinner={false} />
              {children}
              <Toaster />
            </QueryClientProvider>
          </UnitSelectorProvider>
        </OTContextProvider>
      </SettingsContext.Provider>
    </I18nextProvider>
  );
}

export default Providers;
